// Vanuatu National Syllabus: Physics - Senior Secondary (Years 11-13)
// Based on Y11-13(11&12 Review)_Physics_syl_RevOct2023wkshop_FF18April2024_WEB.pdf

export const physicsCurriculum = {
  "Year 11": {
    courseInfo: {
      subject: "Physics",
      level: "Year 11",
      school: "St Patrick's College",
      assessmentWeighting: {
        internalExamination: 60,
        continuousAssessment: 40
      },
      strandWeighting: {
        measurements: 7,
        mechanics: 13,
        electricityMagnetism: 14,
        lightWaves: 15,
        thermalAtomicNuclear: 11
      },
      totalHours: 132,
      hoursPerWeek: 4
    },
    strands: [
      {
        id: "y11-phy-1",
        name: "Measurements",
        code: "PHY1",
        weighting: 7,
        hours: 15,
        percentage: 12,
        majorLearningOutcome: "Demonstrate scientific skills and knowledge of units for measurement in physical quantities, graphical representations and use of mathematics in physics",
        subStrands: [
          {
            id: "11PHY1.1",
            title: "Physical Quantities",
            description: "Understanding fundamental SI units and scientific notation",
            slos: [
              "Express numbers in scientific notation",
              "Express calculation results to the appropriate number of significant figures",
              "Identify the appropriate SI unit for Mass, Time and Length",
              "State the accuracy associated with measurements and calculations",
              "Write different SI units using names and symbols"
            ]
          },
          {
            id: "11PHY1.2",
            title: "Vectors and Scalars",
            description: "Understanding vector and scalar quantities and their operations",
            slos: [
              "Define scalars and vectors",
              "Identify whether a quantity is a scalar or vector (mass, distance, time, speed, velocity, displacement)",
              "Represent vectors by scaled lines and numerically",
              "Add vectors to get a resultant vector",
              "Subtract vectors to get a resultant vector",
              "Distinguish between scalar and vector quantities"
            ]
          },
          {
            id: "11PHY1.3",
            title: "Graphical Representations",
            description: "Scientific investigation skills and graphical analysis",
            slos: [
              "State the topic and aim of an investigation",
              "Describe method of investigation",
              "Collect and record relevant data (independent and dependent variables)",
              "Sketch and label graphs appropriate to the data following standard convention",
              "Plot data on a graph grid and indicate a best-fit line",
              "Calculate the slope of a straight line graph",
              "Make relevant calculations showing correct units and significant figures",
              "Determine the equation of a straight line graph using y = mx + c"
            ]
          }
        ]
      },
      {
        id: "y11-phy-2",
        name: "Mechanics",
        code: "PHY2",
        weighting: 13,
        hours: 29,
        percentage: 21,
        majorLearningOutcome: "Apply knowledge of motion, forces, moments and energy to solve problems in mechanics",
        subStrands: [
          {
            id: "11PHY2.1",
            title: "Motion of Objects",
            description: "Displacement, velocity, acceleration and motion graphs",
            slos: [
              "Define displacement, velocity, change in velocity, and acceleration",
              "Calculate displacement, velocity, and acceleration",
              "Calculate change in velocity/acceleration using vector addition or subtraction",
              "Measure distance and time of a moving object in practical situations",
              "Draw and label displacement-time and velocity-time graphs",
              "Identify gradient of displacement-time graph as velocity",
              "Calculate gradient of displacement-time graph",
              "State gradient of velocity-time graph as acceleration",
              "Identify area under velocity-time graph as displacement",
              "Calculate area under velocity-time graph"
            ]
          },
          {
            id: "11PHY2.2",
            title: "Force",
            description: "Newton's laws of motion and resultant forces",
            slos: [
              "Define resultant force",
              "List the contact forces and field forces",
              "State Newton's first, second, and third laws of motion",
              "Describe effects of a force on an object",
              "Calculate the resultant or unbalanced force on an object moving in a straight line"
            ]
          },
          {
            id: "11PHY2.3",
            title: "Moment",
            description: "Torque and equilibrium",
            slos: [
              "Define moment of a force",
              "Calculate the torque of a force (perpendicular case only)",
              "Solve problems where torques act in opposition and equilibrium is established"
            ]
          },
          {
            id: "11PHY2.4",
            title: "Mechanical Energy",
            description: "Energy forms, work, power, and conservation of energy",
            slos: [
              "Define gravitational potential energy, kinetic energy, work, power, and conservation of energy",
              "Calculate gravitational potential energy and kinetic energy",
              "Measure extension and force on a spring or rubber band",
              "Draw and label a graph of force versus extension",
              "Identify and calculate the slope of force vs extension graph as spring constant",
              "Identify and calculate area under force vs extension graph as energy",
              "Solve complex problems involving conservation of energy",
              "Solve complex problems involving work done and power",
              "Discuss behaviour of rubber band beyond its elastic limit"
            ]
          }
        ]
      },
      {
        id: "y11-phy-3",
        name: "Electricity, Magnetism and Electromagnetism",
        code: "PHY3",
        weighting: 14,
        hours: 30,
        percentage: 23,
        majorLearningOutcome: "Understand electrical circuits, Ohm's law, electrical energy and magnetism",
        subStrands: [
          {
            id: "11PHY3.1",
            title: "Electrical Circuits",
            description: "Circuit components, series and parallel circuits",
            slos: [
              "Define electric circuit, voltage, and current",
              "Identify circuit symbols and list symbols for electrical components",
              "Describe function of ammeter, voltmeter, variable resistor",
              "Draw circuit diagrams for series or parallel circuits",
              "Construct simple series and parallel circuits",
              "Compare parallel and series circuits in terms of current, voltage, and resistance",
              "Explain why ammeters and voltmeters are connected differently in a circuit"
            ]
          },
          {
            id: "11PHY3.2",
            title: "Ohm's Law and Resistance",
            description: "Resistance, Ohm's law, and circuit calculations",
            slos: [
              "Define resistance and ohmic conductor",
              "State factors affecting resistance of wire (temperature, length, thickness)",
              "Describe influence of temperature on resistance of wire",
              "Calculate voltage, current or resistance using Ohm's law",
              "Plot data on a voltage-current graph",
              "Calculate resistance from a voltage-current graph",
              "Calculate total resistance in series and parallel combinations"
            ]
          },
          {
            id: "11PHY3.3",
            title: "Electrical Energy and Power",
            description: "Power calculations, cost of electricity, and renewable energy",
            slos: [
              "Define power as energy per unit time in electrical context",
              "Calculate power output using P=VI, P=V^2/R, or P=I^2R",
              "Calculate electrical energy in kilowatt-hours",
              "Calculate and explain the cost of electricity (UNELCO bill)",
              "Describe the dangers of electricity",
              "Define renewable sources of energy",
              "List renewable energy sources used in Vanuatu (windmill, hydro, solar)",
              "Explain how renewable energy sources produce electrical energy",
              "Discuss need for renewable sources and disadvantages of non-renewable sources in Vanuatu"
            ]
          },
          {
            id: "11PHY3.4",
            title: "Magnetism and Electromagnetism",
            description: "Magnetic fields, electromagnets and applications",
            slos: [
              "Use right hand grip rule to identify direction of magnetic field around a current-carrying wire",
              "Use modified right hand grip rule for solenoid magnetic field direction",
              "Draw magnetic fields around a current-carrying wire and solenoid",
              "Define electromagnet",
              "Explain effects of increasing current, turns, and field on electromagnet strength",
              "Explain how electric bell, relay, reed switch or loudspeaker work"
            ]
          }
        ]
      },
      {
        id: "y11-phy-4",
        name: "Light and Waves",
        code: "PHY4",
        weighting: 15,
        hours: 33,
        percentage: 25,
        majorLearningOutcome: "Understand light propagation, reflection, refraction and wave properties",
        subStrands: [
          {
            id: "11PHY4.1",
            title: "Propagation and Reflection of Light",
            description: "Rectilinear propagation, reflection laws, and applications",
            slos: [
              "Define rectilinear propagation of light and reflection",
              "State examples and laws of reflection",
              "Use ray diagrams to locate image of an object in a plane mirror",
              "Determine angle of incidence or reflection",
              "Describe production of shadows, eclipse and pinhole camera",
              "Explain production of pinhole camera, shadows, and eclipse using rectilinear propagation",
              "Discuss applications of rectilinear propagation in real-life situations"
            ]
          },
          {
            id: "11PHY4.2",
            title: "Refraction of Light",
            description: "Snell's law, critical angle, and total internal reflection",
            slos: [
              "Define refraction of light",
              "State and identify angles of incidence, reflection, and refraction at different media",
              "Describe how light behaves entering an optically denser material",
              "Describe and apply the relationship n1 sin theta1 = n2 sin theta2",
              "Plot and label graphs of n1 sin theta1 against n2 sin theta2",
              "Solve problems involving Snell's law",
              "Explain partial reflection at boundary between media of different optical densities",
              "Explain critical angle conditions and calculate critical angle",
              "Describe total internal reflection",
              "Discuss real-life applications of light refraction"
            ]
          },
          {
            id: "11PHY4.3",
            title: "Wave Properties and Propagation",
            description: "Wave characteristics, types, and the wave equation",
            slos: [
              "Define and describe amplitude, wavelength, and frequency of waves",
              "Calculate average displacement of vibrating particles",
              "Explain that waves carry energy from one position to another",
              "Use the relationship v = f x lambda to calculate unknowns",
              "Illustrate propagation of waves in water",
              "Explain why wave front is perpendicular to direction of travel",
              "Relate differing speeds of sound propagation with qualities of transporting medium",
              "Demonstrate difference between longitudinal and transverse waves through practical activity"
            ]
          }
        ]
      },
      {
        id: "y11-phy-5",
        name: "Thermal, Atomic and Nuclear Physics",
        code: "PHY5",
        weighting: 11,
        hours: 25,
        percentage: 19,
        majorLearningOutcome: "Understand gas laws, atomic structure and radioactivity",
        subStrands: [
          {
            id: "11PHY5.1",
            title: "Thermal Physics",
            description: "Gas laws, pressure, temperature, and ideal gas law",
            slos: [
              "Define Pascal as SI unit of pressure",
              "Define temperature as average kinetic energy of particles",
              "Convert degrees Celsius to Kelvin and vice versa",
              "Describe how forces produce pressure of a contained gas",
              "Explain why gas pressure is inversely proportional to volume at constant temperature",
              "Relate gas pressure variation with absolute temperature at constant volume",
              "Use the combined relationship (ideal gas law) to solve problems"
            ]
          },
          {
            id: "11PHY5.2",
            title: "Atomic and Nuclear Physics",
            description: "Atomic models, isotopes, and radioactivity",
            slos: [
              "State features of Thompson's and Rutherford's atomic models",
              "Describe and compare Thompson's and Rutherford's models",
              "Explain differences and causes of alpha particle deflection in Rutherford's experiment",
              "Draw a model of the atom with protons, neutrons, and electron energy levels",
              "Define mass number, atomic number, and isotope",
              "Identify isotopes in terms of neutrons, mass number and atomic number",
              "Define radioactivity and list three types of radioactive decay",
              "Differentiate between natural and artificial radioactivity",
              "Discuss useful and harmful uses of radioactivity in science and technology"
            ]
          }
        ]
      }
    ]
  },
  "Year 12": {
    courseInfo: {
      subject: "Physics",
      level: "Year 12",
      school: "St Patrick's College",
      assessmentWeighting: {
        externalExamination: 60,
        internalAssessment: 40
      },
      strandWeighting: {
        measurements: 7,
        mechanics: 21,
        electricityMagnetism: 10,
        thermalAtomicNuclear: 13,
        lightWaves: 9
      },
      totalHours: 165,
      hoursPerWeek: 5
    },
    strands: [
      {
        id: "y12-phy-1",
        name: "Measurements",
        code: "PHY1",
        weighting: 7,
        hours: 19,
        percentage: 12,
        majorLearningOutcome: "Apply measurement techniques with uncertainties, vectors in 2D, and data interpretation through interpolation and extrapolation",
        subStrands: [
          {
            id: "12PHY1.1",
            title: "Physical Quantities",
            description: "SI units, derived quantities, uncertainties and errors",
            slos: [
              "Identify and use appropriate SI units for the 7 fundamental quantities",
              "Derive simple SI units for other quantities from the 7 fundamental quantities",
              "Express answers in correct significant figures or SI units",
              "State accuracy (uncertainty) associated with measurements and calculations",
              "Identify correct instruments for various measurements",
              "Express absolute uncertainty to percentage uncertainty and vice versa",
              "Outline procedure to identify volume of a microscope slide",
              "Calculate quantities using addition, subtraction, multiplication and division with uncertainties"
            ]
          },
          {
            id: "12PHY1.2",
            title: "Vectors and Scalars",
            description: "2D vector operations using trigonometry and graphical methods",
            slos: [
              "Distinguish between scalar and vector quantities with examples",
              "State the direction of a vector",
              "Use trigonometry/Pythagoras theorem to add/subtract vectors in 2D",
              "Evaluate direction of resultant vector using trigonometry",
              "Resolve single vector into two components at right angles",
              "Perform addition of vectors at an angle graphically"
            ]
          },
          {
            id: "12PHY1.3",
            title: "Interpolation and Extrapolation of Data",
            description: "Data analysis techniques for graph interpretation",
            slos: [
              "Define extrapolation and interpolation",
              "Distinguish between purposes of interpolation and extrapolation",
              "Sketch and label graphs appropriate to data and purpose",
              "Estimate points on graphs through interpolation and extrapolation",
              "Evaluate reliability of information from interpolation and extrapolation"
            ]
          }
        ]
      },
      {
        id: "y12-phy-2",
        name: "Mechanics",
        code: "PHY2",
        weighting: 21,
        hours: 59,
        percentage: 35,
        majorLearningOutcome: "Apply equations of motion, projectile motion, circular motion, momentum conservation and work-energy principles",
        subStrands: [
          {
            id: "12PHY2.1",
            title: "Motion of the Object",
            description: "Equations of motion, ticker timer analysis, and real-life applications",
            slos: [
              "Differentiate displacement/distance and speed/velocity",
              "Define and compare average velocity and average speed",
              "Describe meaning of negative values in displacement and velocity",
              "Calculate displacement, velocity, acceleration and time using equations of motion",
              "Analyse ticker tapes to calculate instantaneous velocity, average velocity, and acceleration",
              "Plot appropriate graphs from motion data",
              "Discuss applications of displacement, velocity and acceleration to real-life (safe stopping distances, speed limits)"
            ]
          },
          {
            id: "12PHY2.2",
            title: "Projectile Motion",
            description: "Horizontal and vertical components of projectile motion",
            slos: [
              "Sketch and describe the path of an object in projectile motion",
              "Describe changes in velocity components during flight",
              "State horizontal velocity remains constant in projectile motion",
              "Identify vertical velocity is zero at maximum height",
              "Calculate vertical displacements at any time of flight",
              "Define range and calculate range of projectile motion",
              "Resolve horizontal and vertical components of velocity",
              "Use kinematic equations for half projectiles, full projectiles, and projectiles launched from height"
            ]
          },
          {
            id: "12PHY2.3",
            title: "Circular Motion",
            description: "Centripetal force, acceleration, and universal gravitation",
            slos: [
              "State the universal law of gravitation and Newton's Second Law",
              "State formulae for centripetal acceleration (a = v^2/r) and force (F = mv^2/r)",
              "Draw direction of velocity and acceleration in circular motion",
              "Use v = 2 pi r/T to solve circular motion problems",
              "Use a = v^2/r and F = mv^2/r to solve problems",
              "Conduct practical investigation of circular motion in horizontal plane"
            ]
          },
          {
            id: "12PHY2.4",
            title: "Momentum",
            description: "Conservation of momentum, impulse, and collisions",
            slos: [
              "Define momentum and impulse",
              "Calculate momentum of an object with constant acceleration",
              "Calculate change of momentum due to external force",
              "State law of conservation of momentum",
              "Use delta P = F x delta t to solve problems",
              "Use conservation of momentum for 1D collisions",
              "Use conservation of momentum for 2D collisions and explosions",
              "Calculate velocities before and after collisions from ticker tape data",
              "Calculate kinetic energy before and after collisions"
            ]
          },
          {
            id: "12PHY2.5",
            title: "Work, Energy and Power",
            description: "Work done, power, Hooke's law, and work-energy principle",
            slos: [
              "Calculate work done using W = Fs",
              "Determine force component contributing to work done",
              "Calculate power in terms of work done",
              "Calculate work done using force-displacement graph",
              "Explain relationship between area under F-d graph and work done",
              "Calculate slope of force-extension graph and identify spring constant",
              "State work-energy principle",
              "Define Hooke's Law and state it mathematically",
              "Investigate work done and calculate power climbing stairs"
            ]
          }
        ]
      },
      {
        id: "y12-phy-3",
        name: "Electricity, Magnetism and Electromagnetism",
        code: "PHY3",
        weighting: 10,
        hours: 28,
        percentage: 17,
        majorLearningOutcome: "Apply knowledge of DC circuits, electromagnetic induction, and AC circuits",
        subStrands: [
          {
            id: "12PHY3.1",
            title: "Direct Current Circuits",
            description: "Charge, electric fields, and DC circuit analysis",
            slos: [
              "Identify qualities of good and bad conductors",
              "Define direct current",
              "Distinguish direction of conventional current and electron flow",
              "Calculate charge passing through a point (Q = It)",
              "Calculate number of electrons in a given charge",
              "Define electric fields and draw uniform electric fields",
              "Calculate electric field strength (E = V/d)",
              "Calculate work done when charge displaces in electric field"
            ]
          },
          {
            id: "12PHY3.2",
            title: "Ohm's Law and Electrical Energy",
            description: "Resistance, circuit analysis, and energy dissipation",
            slos: [
              "Define resistance and ohmic/non-ohmic conductors",
              "Sketch graphs distinguishing ohmic and non-ohmic conductors",
              "Calculate resistance using colour codes",
              "Use Ohm's law V = IR and explain its limitations",
              "Calculate resistance in combined series-parallel circuits",
              "Calculate current and voltage across each resistor",
              "Apply P = VI and calculate energy using E = VIt",
              "Conduct practical measuring currents and voltages in series and parallel circuits"
            ]
          },
          {
            id: "12PHY3.3",
            title: "Magnetic Effects, Motor Effects and Electromagnetic Induction",
            description: "Magnetic fields, motor effect, and transformers",
            slos: [
              "Describe magnetic fields around conductors (straight wire, circular coil, solenoid)",
              "Describe applications of motor effect (DC motor, loudspeaker, force between parallel conductors)",
              "Calculate force between parallel current-carrying conductors",
              "Identify attraction/repulsion between parallel current-carrying wires",
              "State function of transformer, identify step-up/step-down, explain operation",
              "Describe Eddy currents and minimisation",
              "Explain factors affecting induced current in electromagnetic induction",
              "Demonstrate electromagnetic induction practically",
              "Discuss applications (dynamo, ignition coil, relay, electric bell)"
            ]
          },
          {
            id: "12PHY3.4",
            title: "Alternating Current",
            description: "AC circuits, inductance, capacitance, reactance, and RLC circuits",
            slos: [
              "Define inductance and reactance",
              "State effects of capacitance and inductance on current in AC circuits",
              "Sketch V and I vs time for resistive, capacitive, and inductive circuits",
              "Calculate phase angle, resonant frequency, power, power factor in RLC series",
              "Analyse current growth using t = L/R and calculate stored energy (Ep = 0.5 LI^2)",
              "Calculate transformer turns, voltage and current (Ns/Np = Vs/Vp = Ip/Is)",
              "Define RMS voltage/current, calculate impedance in RLC circuits",
              "Calculate reactance of capacitor (Xc = 1/wC) and inductor (XL = wL)",
              "Use f = 1/(2 pi sqrt(LC)) for resonant frequency",
              "Explain frequency filtering using capacitors/inductors",
              "Design systems for low/high frequency filters"
            ]
          }
        ]
      },
      {
        id: "y12-phy-4",
        name: "Light and Waves",
        code: "PHY4",
        weighting: 9,
        hours: 27,
        percentage: 22,
        majorLearningOutcome: "Apply principles of reflection in spherical mirrors, refraction, wave models, and interference",
        subStrands: [
          {
            id: "12PHY4.1",
            title: "Propagation and Reflection of Light",
            description: "Spherical mirrors, ray diagrams, and mirror equations",
            slos: [
              "Explain properties of reflection in plane and spherical mirrors",
              "Define terms: centre of curvature, radius, pole, focus, focal length",
              "Construct ray diagrams to locate images in mirrors",
              "Describe nature and position of images in plane, concave, and convex mirrors",
              "Calculate magnification of images",
              "Use mirror formula 1/f = 1/u + 1/v",
              "Explain real and virtual image formation in reflection",
              "Conduct practical to identify image positions in different mirrors"
            ]
          },
          {
            id: "12PHY4.2",
            title: "Refraction of Light",
            description: "Snell's law, critical angle, and total internal reflection",
            slos: [
              "Define refraction and identify angles at boundaries",
              "Describe light behaviour entering optically denser material",
              "Apply n1 sin theta1 = n2 sin theta2 to solve problems",
              "Plot graphs and calculate refractive indices",
              "Explain partial reflection at optical boundaries",
              "Calculate critical angle and describe total internal reflection",
              "Discuss real-life applications of refraction"
            ]
          },
          {
            id: "12PHY4.3",
            title: "Wave Models",
            description: "Particle and wave models of light, dual nature",
            slos: [
              "Define wave as propagation of a disturbance",
              "Draw reflection and refraction of wave fronts at boundaries",
              "Demonstrate dispersion of white light",
              "State phenomena supporting particle and wave models of light",
              "Explain why blue light bends more than red upon refraction",
              "Explain photon model and dual nature of light",
              "Discuss failure of corpuscular model and applications of wave model"
            ]
          },
          {
            id: "12PHY4.4",
            title: "Diffraction and Interference of Waves",
            description: "Superposition, Young's double-slit experiment",
            slos: [
              "Use superposition principle to draw resultant wave shapes",
              "Differentiate constructive and destructive superposition",
              "Define and explain diffraction and interference",
              "Label nodes and antinodes in interference patterns",
              "Use relationships lambda = delta x/L and d sin theta = (n-0.5) lambda",
              "Explain and apply Young's double-slit experiment relationships"
            ]
          }
        ]
      },
      {
        id: "y12-phy-5",
        name: "Thermal, Atomic and Nuclear Physics",
        code: "PHY5",
        weighting: 13,
        hours: 22,
        percentage: 14,
        majorLearningOutcome: "Apply gas laws, specific heat capacity, latent heat, and nuclear physics concepts",
        subStrands: [
          {
            id: "12PHY5.1",
            title: "Thermal Physics",
            description: "Gas laws, thermometers, specific heat capacity and latent heat",
            slos: [
              "Use ideal gas law to determine unknowns",
              "Interpret cooling/heating curves for latent heat and changes of state",
              "Gather, present and summarise information about temperature scales and thermometers",
              "Explain absolute zero and absolute temperature in temperature scales",
              "Convert Celsius to Kelvin and vice versa",
              "Define latent heat and specific heat capacity",
              "Explain applications of specific heat capacity (land/sea breezes, engine cooling, cooking)",
              "Solve problems related to specific heat capacity and latent heat",
              "Conduct practical to determine specific heat capacity using calorimeters",
              "Account for sources of errors and identify minimisation methods"
            ]
          },
          {
            id: "12PHY5.2",
            title: "Atomic and Nuclear Physics",
            description: "Radioactive decay, half-life, and nuclear equations",
            slos: [
              "Use symbols for alpha, beta particles and gamma rays",
              "Write nuclear equations for alpha, beta and gamma emissions",
              "Explain decay reactions of alpha, beta particles and gamma rays",
              "Determine paths of alpha, beta and gamma through magnetic fields",
              "Compare penetrating distances and ionizing powers",
              "Define and describe half-life and radioactive decay properties",
              "Determine half-life from decay curve",
              "Calculate substance remaining after radioactive decay",
              "Determine particle emission using conservation rules",
              "List consumer products containing radioactive substances",
              "Describe issues related to storing radioactive substances"
            ]
          }
        ]
      }
    ]
  },
  "Year 13": {
    courseInfo: {
      subject: "Physics",
      level: "Year 13",
      school: "St Patrick's College",
      assessmentWeighting: {
        externalExamination: 60,
        internalAssessment: 40
      },
      iaStructure: {
        task1: 15,
        task2: 15,
        task3: 10
      },
      strandWeighting: {
        measurements: 5,
        mechanics: 10,
        electricityMagnetism: 15,
        lightWaves: 15,
        thermalAtomicNuclear: 15
      },
      totalHours: 165,
      hoursPerWeek: 5
    },
    strands: [
      {
        id: "y13-phy-1",
        name: "Measurements",
        code: "PHY1",
        weighting: 5,
        hours: 10,
        percentage: 6,
        majorLearningOutcome: "Apply advanced measurement techniques, vector operations, and conduct full practical investigations",
        subStrands: [
          {
            id: "13PHY1.1",
            title: "Physical Quantities",
            description: "Advanced SI units and uncertainty calculations",
            slos: [
              "Express numbers in scientific notation with correct significant figures",
              "Identify appropriate SI for different measures",
              "State accuracy associated with measurements and calculations",
              "Write different SI units using names and symbols",
              "Calculate quantities using multiplication, division, scalars and exponents with uncertainties"
            ]
          },
          {
            id: "13PHY1.2",
            title: "Vectors",
            description: "Advanced vector operations in 2D including trigonometric resolution",
            slos: [
              "Define and identify vectors and scalars",
              "Resolve single vectors into two perpendicular components by construction",
              "Combine two vectors to determine magnitude and direction of resultant",
              "Determine change value in a vector during a particular period",
              "Resolve vectors into components using trigonometry",
              "Interpret vector diagrams in real-life situations",
              "Perform addition and subtraction of non-perpendicular vectors for complex problems"
            ]
          },
          {
            id: "13PHY1.3",
            title: "Practical Investigation",
            description: "Complete scientific investigation and reporting",
            slos: [
              "State topic, aim, rationale and background of investigation",
              "List equipment and describe method of investigation",
              "Identify dependent and independent variables",
              "Collect data with uncertainties",
              "Plot linear and non-linear graphs, transform non-linear to linear",
              "Determine linear equations in form y = mx + c",
              "Show relevant calculations with units, significant figures and uncertainties",
              "Draw valid conclusions justified by evidence",
              "Compare results to supplied theory",
              "Provide critical evaluation of results",
              "Present complete report with references and logbook",
              "List possible sources of errors and explain how to minimize them",
              "Make inferences and recommendations from investigation results",
              "Evaluate reliability, validity, limitations and suggest improvements"
            ]
          }
        ]
      },
      {
        id: "y13-phy-2",
        name: "Mechanics",
        code: "PHY2",
        weighting: 10,
        hours: 43,
        percentage: 26,
        majorLearningOutcome: "Apply advanced mechanics including centre of mass, circular motion, rotational motion, and SHM",
        subStrands: [
          {
            id: "13PHY2.1",
            title: "Translational Motion",
            description: "Centre of mass, momentum, impulse, and collision analysis",
            slos: [
              "Define centre of mass and calculate its position: r = (m1r1 + m2r2)/(m1+m2)",
              "Describe motion of centre of mass during collision using conservation of momentum",
              "Calculate velocity of centre of mass (total momentum / total mass)",
              "Define momentum and impulse; describe in terms of Newton's 2nd law",
              "Describe qualitatively collisions (moving with stationary, opposite direction, same direction)",
              "Explain momentum conservation using Newton's Third Law",
              "Analyse collisions and explosions using conservation of momentum",
              "Discuss safety implications of high-speed collisions (low speed zones, airbags)"
            ]
          },
          {
            id: "13PHY2.2",
            title: "Circular Motion",
            description: "Vertical circles, conical pendulum, and banked curves",
            slos: [
              "Identify types of forces providing centripetal force in real-life",
              "Identify applications of horizontal/vertical circles",
              "Calculate tension at highest/lowest points in vertical circles using Newton's Second Law",
              "Calculate minimum/maximum velocity in vertical circles",
              "Use free-body diagrams for conical pendulum and banked/unbanked curves",
              "Derive and calculate formulas for vertical circles, conical pendulums, and banked curves",
              "Relate centripetal force to orbital motion of planets and satellites",
              "Predict effects of speed changes on vehicles at banked corners"
            ]
          },
          {
            id: "13PHY2.3",
            title: "Rotational Motion",
            description: "Angular kinematics, torque, rotational energy, and angular momentum",
            slos: [
              "Define angular displacement, angular velocity, and angular acceleration",
              "Use rotational equations (arc length = r theta, omega = delta theta/delta t, etc.)",
              "Derive equations using analogy between translational and rotational motion",
              "Apply Newton's second law: tau = I alpha for rotational motion",
              "Describe rotational inertia in terms of mass distribution",
              "Define and calculate rotational kinetic energy",
              "Calculate total energy of rolling objects (translational + rotational)",
              "Apply conservation of energy to rotational problems",
              "Define and calculate angular momentum (L = mvr and L = I omega)",
              "Describe and apply conservation of angular momentum",
              "Differentiate translational and rotational motion concepts"
            ]
          },
          {
            id: "13PHY2.4",
            title: "Simple Harmonic Motion",
            description: "SHM equations, energy analysis, damping, and resonance",
            slos: [
              "Define SHM and state its conditions",
              "Use reference circle and phasor diagrams for SHM analysis",
              "State and use SHM equations for displacement, velocity, and acceleration",
              "Calculate displacement, velocity, acceleration, time, and angular frequency",
              "Calculate angular frequency using omega = sqrt(k/m) for springs",
              "Describe oscillations in terms of amplitude, period, and frequency",
              "Analyse SHM in terms of potential and kinetic energy",
              "Calculate PE, KE, and total energy in SHM; represent graphically",
              "Define underdamped, overdamped, and critically damped oscillations",
              "Describe effects of damping, forced vibrations, and resonance on SHM",
              "Relate SHM to reference circle and circular motion",
              "Explain practical SHM situations (spring, pendulum, tides)",
              "Verify period-mass or period-length relationships through practical activity"
            ]
          }
        ]
      },
      {
        id: "y13-phy-3",
        name: "Electricity, Magnetism and Electromagnetism",
        code: "PHY3",
        weighting: 15,
        hours: 43,
        percentage: 26,
        majorLearningOutcome: "Apply Kirchhoff's laws, capacitance, electromagnetic induction, and AC circuit analysis",
        subStrands: [
          {
            id: "13PHY3.1",
            title: "Direct Current Circuits",
            description: "Resistivity, Kirchhoff's laws, and EMF with internal resistance",
            slos: [
              "Define resistivity and use r = rho(L/A)",
              "Derive and use E = rho J for electric field",
              "State and write Kirchhoff's Voltage and Current Laws",
              "Apply Kirchhoff's laws to solve unknowns in complex circuits",
              "Analyse circuits with more than one source of EMF",
              "Calculate internal resistance and EMF of a cell"
            ]
          },
          {
            id: "13PHY3.2",
            title: "Capacitance",
            description: "Capacitor operation, charging/discharging, time constant, and energy storage",
            slos: [
              "Define capacitance and describe capacitor operation",
              "Use Q = CV to calculate charge stored",
              "Define dielectric constant and state effect of adding dielectric",
              "Use C = k epsilon0 (A/d) to solve problems",
              "Describe charging process in terms of voltage and current changes",
              "Define time constant and analyse charging using tau = RC",
              "Calculate charge, current, and voltage variation with time",
              "Sketch charge/discharge graphs of V, Q, and I vs time",
              "Use Ep = 0.5QV = 0.5CV^2 for energy stored",
              "Calculate total capacitance in series, parallel, and combinations",
              "Discuss charging/discharging and timing circuit applications"
            ]
          },
          {
            id: "13PHY3.3",
            title: "Electromagnetic Induction",
            description: "Magnetic flux, Faraday's law, Lenz's law, and practical applications",
            slos: [
              "Define magnetic flux and use Phi = BA cos theta",
              "State Fleming's Left Hand Rule for force direction",
              "Derive and use V = Blv sin theta for induced EMF",
              "State and write Faraday's Law mathematically",
              "Calculate EMF, current, and magnetic fields using Faraday's Law",
              "Use epsilon = delta Phi / delta t for induced voltage",
              "State Lenz's law and use it for polarity of induced EMF",
              "Calculate induced voltage in rotating coil in uniform magnetic field",
              "Calculate induced power and energy dissipated by conductors/loops",
              "Develop a model using principles of electromagnetic induction"
            ]
          },
          {
            id: "13PHY3.4",
            title: "Alternating Current",
            description: "AC circuits, RLC analysis, impedance, and power factor",
            slos: [
              "Define inductance and reactance",
              "State effects of capacitance and inductance on AC current",
              "Sketch V and I vs time for resistive, capacitive, and inductive circuits",
              "Calculate phase angle, resonant frequency, power, power factor in RLC",
              "Analyse current growth (t = L/R) and stored energy (Ep = 0.5LI^2)",
              "Calculate transformer parameters (Ns/Np = Vs/Vp = Ip/Is)",
              "Define RMS voltage/current in terms of peak values",
              "Calculate impedance, reactance (Xc, XL), and RMS values",
              "Describe resonance in tuned LCR circuits",
              "Use f = 1/(2 pi sqrt(LC)) for resonant frequency",
              "Design systems for frequency filtering in AC circuits"
            ]
          }
        ]
      },
      {
        id: "y13-phy-4",
        name: "Light and Waves",
        code: "PHY4",
        weighting: 15,
        hours: 33,
        percentage: 20,
        majorLearningOutcome: "Apply advanced wave concepts including interference, standing waves, and Doppler effect",
        subStrands: [
          {
            id: "13PHY4.1",
            title: "Types of Waves",
            description: "Wave properties, electromagnetic spectrum, and CRO",
            slos: [
              "Define wave characteristics: wavelength, amplitude, speed",
              "Define longitudinal and transverse waves",
              "Identify variables affecting wave speed",
              "Solve wave problems involving speed, distance and time",
              "Describe mathematical equation of a wave",
              "Describe cathode ray oscilloscope and its uses",
              "Describe properties and applications of electromagnetic waves",
              "Predict effects of medium changes on wave properties"
            ]
          },
          {
            id: "13PHY4.2",
            title: "Diffraction and Interference",
            description: "Double-slit and multiple-source interference, diffraction gratings",
            slos: [
              "Define diffraction and interference",
              "Identify nodes and antinodes on interference patterns",
              "Describe double-source and multiple-source interference",
              "Describe diffraction gratings and lasers",
              "Draw interference pattern for Young's double-slit experiment",
              "Use Young's equations for constructive and destructive interference",
              "Apply formulae to find unknowns from Young's experiment",
              "Predict effects of variable changes on interference patterns"
            ]
          },
          {
            id: "13PHY4.3",
            title: "Standing Waves",
            description: "Standing waves in strings and pipes, harmonics, and beats",
            slos: [
              "Define standing waves including nodes and antinodes",
              "Describe sound characteristics: music, tone, timbre, beat, harmonic, resonance",
              "Draw wave patterns for open pipes, closed pipes, and strings",
              "Calculate frequency/time of standing waves",
              "Explain harmonic series for string instruments",
              "Define and calculate resonant frequency",
              "Describe how beats are generated and calculate beat frequency",
              "Apply formulae and graphical methods to find unknowns",
              "Explain differences in loudness, pitch, and timbre"
            ]
          },
          {
            id: "13PHY4.4",
            title: "Doppler Effect",
            description: "Doppler effect for sound, water and light waves",
            slos: [
              "Define Doppler Effect",
              "Explain Doppler effect for electromagnetic and sound waves",
              "Describe applications including sonic boom and red shift",
              "Write the apparent frequency formula",
              "Identify direction of moving source from ripple patterns",
              "Explain differences in actual and apparent frequencies for approaching/receding sources",
              "Calculate apparent frequency for moving source or moving observer",
              "Explain effects of speed differences of sound and light using real-life examples"
            ]
          }
        ]
      },
      {
        id: "y13-phy-5",
        name: "Thermal, Atomic and Nuclear Physics",
        code: "PHY5",
        weighting: 15,
        hours: 36,
        percentage: 22,
        majorLearningOutcome: "Apply Bohr's model, energy levels, nuclear fission and fusion concepts",
        subStrands: [
          {
            id: "13PHY5.1",
            title: "Atomic Physics",
            description: "Bohr's model, energy levels, spectral lines, and photoelectric effect",
            slos: [
              "State postulates and limitations of Bohr's theory",
              "Describe Bohr model of hydrogen atom and planetary model",
              "Describe Bohr postulates including photon model and quantization of energy",
              "Describe atomic line spectra and photoelectric effect",
              "Describe particle/wave duality of light",
              "Describe properties of nuclei including charges, mass, radius, stability",
              "Describe origin of nuclear energy through radioactive decay and mass deficit",
              "Discuss postulates and limitations of Bohr's theory",
              "Identify ionisation energy of hydrogen atom as 13.6 eV",
              "Illustrate energy states using energy-level diagram (Lyman, Balmer, Paschen, Brackett series)"
            ]
          },
          {
            id: "13PHY5.2",
            title: "Nuclear Physics",
            description: "Energy levels, Rydberg's formula, binding energy, fission and fusion",
            slos: [
              "Draw and interpret energy level diagrams",
              "Use Rydberg's formula to calculate wavelength of emitted photons",
              "Calculate wavelengths for given frequencies in a spectrum",
              "Identify spectral region of emitted photons from hydrogen atom",
              "State Einstein relation of mass-energy",
              "Define binding energy of the nucleus",
              "Define nuclear fission and fusion",
              "Describe nuclear fission process and find energy freed",
              "Balance equations for nuclear reactions",
              "Distinguish between controlled and uncontrolled fission reactions",
              "Discuss impact of practical fusion energy and safety measures",
              "Discuss fission fuel reactions and environmental impact"
            ]
          }
        ]
      }
    ]
  }
};

// Scheme of Work - aligned with updated Vanuatu National Syllabus
export const physicsSchemeOfWork = {
  "Year 11": {
    term1: [
      { week: "1-2", topic: "Physical Quantities and SI Units", outcomes: "Express numbers in scientific notation, identify SI units for mass, time, length", activities: "Unit conversion exercises, practical measurements" },
      { week: "3-4", topic: "Vectors and Scalars", outcomes: "Define scalars and vectors, add and subtract vectors", activities: "Vector diagrams, scaled line representations" },
      { week: "5-6", topic: "Graphical Representations", outcomes: "Plot data, calculate slope, determine equation of straight line", activities: "Graphing exercises, data interpretation, best-fit lines" },
      { week: "7-9", topic: "Motion of Objects", outcomes: "Define displacement, velocity, acceleration; draw and interpret motion graphs", activities: "Ticker timer practicals, motion graph analysis" },
      { week: "10", topic: "Assessment", outcomes: "Test on Measurements and Motion", activities: "Unit test, practical assessment" }
    ],
    term2: [
      { week: "1-3", topic: "Force and Newton's Laws", outcomes: "State Newton's three laws, calculate resultant force", activities: "Force problems, free-body diagrams" },
      { week: "4-5", topic: "Moments and Equilibrium", outcomes: "Define moment of a force, calculate torque, solve equilibrium problems", activities: "Lever experiments, balance problems" },
      { week: "6-9", topic: "Mechanical Energy", outcomes: "Calculate PE, KE, work, power; apply conservation of energy", activities: "Spring experiments, energy calculations" },
      { week: "10", topic: "Assessment", outcomes: "Test on Mechanics", activities: "Unit test, practical investigation" }
    ],
    term3: [
      { week: "1-3", topic: "Electrical Circuits", outcomes: "Draw circuit diagrams, construct series and parallel circuits", activities: "Circuit building, ammeter and voltmeter measurements" },
      { week: "4-6", topic: "Ohm's Law and Resistance", outcomes: "Apply V=IR, calculate total resistance in series/parallel", activities: "Resistance experiments, voltage-current graphs" },
      { week: "7-8", topic: "Electrical Energy and Power", outcomes: "Calculate power (P=VI), electrical energy in kWh, cost of electricity", activities: "Power calculations, UNELCO bill analysis, renewable energy discussion" },
      { week: "9-10", topic: "Magnetism and Electromagnetism", outcomes: "Apply right hand grip rule, explain electromagnet applications", activities: "Magnetic field mapping, electromagnet experiments" }
    ],
    term4: [
      { week: "1-3", topic: "Propagation and Reflection of Light", outcomes: "Apply laws of reflection, locate images in plane mirrors", activities: "Ray diagrams, pinhole camera, shadow/eclipse demonstrations" },
      { week: "4-6", topic: "Refraction of Light", outcomes: "Apply Snell's law, explain critical angle and total internal reflection", activities: "Refraction experiments, Snell's law calculations" },
      { week: "7-8", topic: "Wave Properties and Thermal Physics", outcomes: "Use v = f x lambda; apply gas laws and temperature conversions", activities: "Wave equation problems, gas law experiments" },
      { week: "9-10", topic: "Atomic Physics, Revision and Examination", outcomes: "Describe atomic models, radioactivity; review all strands", activities: "Past papers, revision, internal examination" }
    ]
  },
  "Year 12": {
    term1: [
      { week: "1-2", topic: "Physical Quantities and Uncertainties", outcomes: "Use SI units, calculate absolute and percentage uncertainties", activities: "Uncertainty calculations, measurement practicals" },
      { week: "3-4", topic: "Vectors in 2D and Data Analysis", outcomes: "Resolve vectors using trigonometry, interpolate and extrapolate data", activities: "Vector resolution exercises, graph interpretation" },
      { week: "5-7", topic: "Equations of Motion and Projectile Motion", outcomes: "Apply kinematic equations, analyse projectile trajectories", activities: "Ticker tape analysis, projectile calculations" },
      { week: "8-10", topic: "Circular Motion", outcomes: "Apply centripetal force and acceleration formulas", activities: "Circular motion experiments, problem solving" }
    ],
    term2: [
      { week: "1-3", topic: "Momentum and Collisions", outcomes: "Apply conservation of momentum for 1D and 2D collisions", activities: "Collision experiments, ticker tape analysis" },
      { week: "4-6", topic: "Work, Energy and Power", outcomes: "Calculate work done, apply Hooke's law and work-energy principle", activities: "Spring experiments, power investigations" },
      { week: "7-9", topic: "DC Circuits and Electric Fields", outcomes: "Calculate charge, electric field strength; analyse DC circuits", activities: "Circuit analysis, electric field calculations" },
      { week: "10", topic: "Assessment", outcomes: "Test on Mechanics and Circuits", activities: "Major test, practical assessment" }
    ],
    term3: [
      { week: "1-3", topic: "Ohm's Law and Electrical Energy", outcomes: "Analyse ohmic/non-ohmic conductors, calculate power and energy", activities: "Series-parallel circuit practicals" },
      { week: "4-6", topic: "Magnetic Effects, Motor Effect and EM Induction", outcomes: "Describe motor effect, transformer operation, electromagnetic induction", activities: "Motor experiments, transformer calculations" },
      { week: "7-9", topic: "Reflection in Spherical Mirrors and Refraction", outcomes: "Use mirror formula, apply Snell's law", activities: "Ray diagrams, mirror practicals, refraction experiments" },
      { week: "10", topic: "Assessment", outcomes: "Test on Electricity and Light", activities: "Unit test, practical investigation" }
    ],
    term4: [
      { week: "1-3", topic: "Wave Models, Diffraction and Interference", outcomes: "Explain wave-particle duality, apply Young's double-slit equations", activities: "Double-slit experiments, interference pattern analysis" },
      { week: "4-5", topic: "AC Circuits", outcomes: "Define inductance, reactance; analyse RLC circuits", activities: "AC circuit analysis, impedance calculations" },
      { week: "6-7", topic: "Thermal Physics", outcomes: "Apply gas laws, specific heat capacity and latent heat", activities: "Calorimetry experiments, gas law problems" },
      { week: "8-9", topic: "Atomic and Nuclear Physics", outcomes: "Write nuclear equations, determine half-life from decay curves", activities: "Decay calculations, nuclear equation balancing" },
      { week: "10", topic: "Revision and Examination", outcomes: "Review all strands", activities: "Past papers, exam preparation" }
    ]
  },
  "Year 13": {
    term1: [
      { week: "1-2", topic: "Advanced Measurements and Vectors", outcomes: "Calculate uncertainties with operations, resolve non-perpendicular vectors", activities: "Complex vector problems, uncertainty propagation" },
      { week: "3-5", topic: "Translational Motion and Centre of Mass", outcomes: "Calculate centre of mass, apply momentum and impulse in collisions", activities: "Collision analysis, momentum conservation problems" },
      { week: "6-8", topic: "Circular Motion (Advanced)", outcomes: "Analyse vertical circles, conical pendulums, banked curves", activities: "Free-body diagrams, banked curve calculations" },
      { week: "9-10", topic: "Assessment", outcomes: "Test on Measurements and Advanced Mechanics", activities: "Unit test, research essay" }
    ],
    term2: [
      { week: "1-4", topic: "Rotational Motion", outcomes: "Apply rotational kinematics, calculate moment of inertia and angular momentum", activities: "Rotational motion experiments, rolling object calculations" },
      { week: "5-8", topic: "Simple Harmonic Motion", outcomes: "Apply SHM equations, analyse damping and resonance", activities: "Pendulum and spring practicals, energy analysis" },
      { week: "9-10", topic: "DC Circuits (Advanced)", outcomes: "Apply Kirchhoff's laws, calculate internal resistance and EMF", activities: "Complex circuit analysis" }
    ],
    term3: [
      { week: "1-3", topic: "Capacitance", outcomes: "Apply Q=CV, analyse charging/discharging with time constant", activities: "RC circuit experiments, capacitor calculations" },
      { week: "4-6", topic: "Electromagnetic Induction", outcomes: "Apply Faraday's and Lenz's laws, calculate induced EMF", activities: "EM induction experiments, rotating coil calculations" },
      { week: "7-8", topic: "AC Circuits (Advanced)", outcomes: "Calculate impedance, resonant frequency, power factor in RLC circuits", activities: "RLC circuit analysis, frequency filter design" },
      { week: "9-10", topic: "Assessment", outcomes: "Test on Electricity and Magnetism", activities: "Unit test, practical investigation" }
    ],
    term4: [
      { week: "1-2", topic: "Types of Waves and EM Spectrum", outcomes: "Solve wave problems, describe CRO and EM spectrum applications", activities: "Wave equation problems, CRO demonstrations" },
      { week: "3-4", topic: "Diffraction, Interference and Standing Waves", outcomes: "Apply Young's equations, calculate harmonics and beat frequency", activities: "Double-slit experiments, standing wave practicals" },
      { week: "5-6", topic: "Doppler Effect", outcomes: "Calculate apparent frequency for moving source/observer", activities: "Doppler calculations, real-life applications" },
      { week: "7-8", topic: "Atomic and Nuclear Physics", outcomes: "Apply Bohr's model, Rydberg's formula, binding energy, fission and fusion", activities: "Energy level diagrams, nuclear calculations" },
      { week: "9-10", topic: "Practical Investigation, Revision and Examination", outcomes: "Complete investigation report; comprehensive review", activities: "Investigation write-up, past papers, exam preparation" }
    ]
  }
};

// Yearly Planner - aligned with updated Vanuatu National Syllabus
export const physicsYearlyPlanner = {
  "Year 11": {
    term1: {
      weeks: "Weeks 1-10",
      focus: "PHY1 & PHY2: Measurements and Basic Mechanics",
      units: [
        { title: "Physical Quantities and Units", weeks: "Week 1-2", strand: "PHY1", subStrand: "1.1", topics: ["SI Units", "Scientific Notation", "Significant Figures"] },
        { title: "Vectors and Scalars", weeks: "Week 3-4", strand: "PHY1", subStrand: "1.2", topics: ["Vector Addition", "Vector Subtraction"] },
        { title: "Graphical Representations", weeks: "Week 5-6", strand: "PHY1", subStrand: "1.3", topics: ["Graphs", "Gradients", "y = mx + c"] },
        { title: "Motion of Objects", weeks: "Week 7-10", strand: "PHY2", subStrand: "2.1", topics: ["Displacement", "Velocity", "Acceleration", "Motion Graphs"] }
      ],
      assessment: "Unit Test + Continuous Assessment"
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "PHY2: Forces, Moments, Energy",
      units: [
        { title: "Force and Newton's Laws", weeks: "Week 1-3", strand: "PHY2", subStrand: "2.2", topics: ["Resultant Force", "Newton's Laws"] },
        { title: "Moments and Equilibrium", weeks: "Week 4-5", strand: "PHY2", subStrand: "2.3", topics: ["Torque", "Equilibrium"] },
        { title: "Mechanical Energy", weeks: "Week 6-10", strand: "PHY2", subStrand: "2.4", topics: ["PE", "KE", "Work", "Power", "Conservation of Energy"] }
      ],
      assessment: "Unit Test + Practical Assessment"
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "PHY3: Electricity, Magnetism and Electromagnetism",
      units: [
        { title: "Electrical Circuits", weeks: "Week 1-3", strand: "PHY3", subStrand: "3.1", topics: ["Circuit Diagrams", "Series", "Parallel"] },
        { title: "Ohm's Law and Resistance", weeks: "Week 4-6", strand: "PHY3", subStrand: "3.2", topics: ["V=IR", "Total Resistance"] },
        { title: "Electrical Energy and Power", weeks: "Week 7-8", strand: "PHY3", subStrand: "3.3", topics: ["P=VI", "kWh", "Renewable Energy"] },
        { title: "Magnetism and Electromagnetism", weeks: "Week 9-10", strand: "PHY3", subStrand: "3.4", topics: ["Magnetic Fields", "Electromagnets"] }
      ],
      assessment: "Unit Test + Practical Investigation"
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "PHY4 & PHY5: Light, Waves, Thermal, Atomic",
      units: [
        { title: "Light: Propagation and Reflection", weeks: "Week 1-3", strand: "PHY4", subStrand: "4.1", topics: ["Reflection Laws", "Plane Mirrors", "Shadows"] },
        { title: "Refraction of Light", weeks: "Week 4-6", strand: "PHY4", subStrand: "4.2", topics: ["Snell's Law", "Critical Angle", "Total Internal Reflection"] },
        { title: "Waves and Thermal Physics", weeks: "Week 7-8", strand: "PHY4-PHY5", subStrand: "4.3-5.1", topics: ["Wave Equation", "Gas Laws"] },
        { title: "Atomic Physics, Revision & Exam", weeks: "Week 9-10", strand: "PHY5", subStrand: "5.2", topics: ["Atomic Models", "Radioactivity", "Review"] }
      ],
      assessment: "Internal Examination (60%)"
    }
  },
  "Year 12": {
    term1: {
      weeks: "Weeks 1-10",
      focus: "PHY1 & PHY2: Measurements, Projectile and Circular Motion",
      units: [
        { title: "Physical Quantities and Uncertainties", weeks: "Week 1-2", strand: "PHY1", subStrand: "1.1", topics: ["Uncertainties", "Percentage Error"] },
        { title: "Vectors in 2D and Data Analysis", weeks: "Week 3-4", strand: "PHY1", subStrand: "1.2-1.3", topics: ["Trigonometric Resolution", "Interpolation", "Extrapolation"] },
        { title: "Equations of Motion and Projectile Motion", weeks: "Week 5-7", strand: "PHY2", subStrand: "2.1-2.2", topics: ["Kinematic Equations", "Projectile Trajectories"] },
        { title: "Circular Motion", weeks: "Week 8-10", strand: "PHY2", subStrand: "2.3", topics: ["Centripetal Force", "Universal Gravitation"] }
      ],
      assessment: "Unit Test + Assignment"
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "PHY2 & PHY3: Momentum, Energy and DC Circuits",
      units: [
        { title: "Momentum and Collisions", weeks: "Week 1-3", strand: "PHY2", subStrand: "2.4", topics: ["Impulse", "1D/2D Collisions", "Conservation"] },
        { title: "Work, Energy and Power", weeks: "Week 4-6", strand: "PHY2", subStrand: "2.5", topics: ["Work Done", "Hooke's Law", "Work-Energy Principle"] },
        { title: "DC Circuits and Electric Fields", weeks: "Week 7-9", strand: "PHY3", subStrand: "3.1", topics: ["Charge Q=It", "Electric Field E=V/d"] },
        { title: "Assessment", weeks: "Week 10", strand: "PHY2-PHY3", subStrand: "-", topics: ["Major Test"] }
      ],
      assessment: "Major Test + Practical"
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "PHY3 & PHY4: Circuits, EM Induction, and Optics",
      units: [
        { title: "Ohm's Law and Electrical Energy", weeks: "Week 1-3", strand: "PHY3", subStrand: "3.2", topics: ["Ohmic Conductors", "Series-Parallel Circuits"] },
        { title: "Magnetic Effects and EM Induction", weeks: "Week 4-6", strand: "PHY3", subStrand: "3.3", topics: ["Motor Effect", "Transformers", "Faraday's Law"] },
        { title: "Spherical Mirrors and Refraction", weeks: "Week 7-9", strand: "PHY4", subStrand: "4.1-4.2", topics: ["Mirror Formula", "Snell's Law"] },
        { title: "Assessment", weeks: "Week 10", strand: "PHY3-PHY4", subStrand: "-", topics: ["Unit Test"] }
      ],
      assessment: "Unit Test + Investigation"
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "PHY3, PHY4, PHY5: AC, Waves, Thermal, Nuclear",
      units: [
        { title: "Wave Models, Diffraction and Interference", weeks: "Week 1-3", strand: "PHY4", subStrand: "4.3-4.4", topics: ["Wave-Particle Duality", "Young's Experiment"] },
        { title: "AC Circuits", weeks: "Week 4-5", strand: "PHY3", subStrand: "3.4", topics: ["Inductance", "Reactance", "RLC Circuits"] },
        { title: "Thermal and Nuclear Physics", weeks: "Week 6-8", strand: "PHY5", subStrand: "5.1-5.2", topics: ["Gas Laws", "Latent Heat", "Half-life", "Decay"] },
        { title: "Revision & Exam", weeks: "Week 9-10", strand: "All", subStrand: "-", topics: ["External Exam Prep"] }
      ],
      assessment: "External Examination (60%)"
    }
  },
  "Year 13": {
    term1: {
      weeks: "Weeks 1-10",
      focus: "PHY1 & PHY2: Advanced Measurements and Mechanics",
      units: [
        { title: "Advanced Measurements and Vectors", weeks: "Week 1-2", strand: "PHY1", subStrand: "1.1-1.2", topics: ["Uncertainty Propagation", "Non-Perpendicular Vectors"] },
        { title: "Translational Motion", weeks: "Week 3-5", strand: "PHY2", subStrand: "2.1", topics: ["Centre of Mass", "Momentum", "Impulse"] },
        { title: "Circular Motion (Advanced)", weeks: "Week 6-8", strand: "PHY2", subStrand: "2.2", topics: ["Vertical Circles", "Conical Pendulum", "Banked Curves"] },
        { title: "Assessment", weeks: "Week 9-10", strand: "PHY1-PHY2", subStrand: "-", topics: ["Unit Test", "Research Essay"] }
      ],
      assessment: "Unit Test + Research Essay"
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "PHY2 & PHY3: Rotational Motion, SHM, DC Circuits",
      units: [
        { title: "Rotational Motion", weeks: "Week 1-4", strand: "PHY2", subStrand: "2.3", topics: ["Angular Kinematics", "Moment of Inertia", "Angular Momentum"] },
        { title: "Simple Harmonic Motion", weeks: "Week 5-8", strand: "PHY2", subStrand: "2.4", topics: ["SHM Equations", "Energy", "Damping", "Resonance"] },
        { title: "DC Circuits (Advanced)", weeks: "Week 9-10", strand: "PHY3", subStrand: "3.1", topics: ["Kirchhoff's Laws", "Internal Resistance"] }
      ],
      assessment: "Major Test + Practical"
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "PHY3: Capacitance, EM Induction, AC Circuits",
      units: [
        { title: "Capacitance", weeks: "Week 1-3", strand: "PHY3", subStrand: "3.2", topics: ["Q=CV", "Time Constant", "Energy Storage"] },
        { title: "Electromagnetic Induction", weeks: "Week 4-6", strand: "PHY3", subStrand: "3.3", topics: ["Faraday's Law", "Lenz's Law", "Induced EMF"] },
        { title: "AC Circuits (Advanced)", weeks: "Week 7-8", strand: "PHY3", subStrand: "3.4", topics: ["Impedance", "Resonance", "Frequency Filters"] },
        { title: "Assessment", weeks: "Week 9-10", strand: "PHY3", subStrand: "-", topics: ["Unit Test", "Practical Investigation"] }
      ],
      assessment: "Unit Test + Investigation"
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "PHY4 & PHY5: Waves, Doppler, Atomic, Nuclear",
      units: [
        { title: "Waves, Diffraction and Standing Waves", weeks: "Week 1-4", strand: "PHY4", subStrand: "4.1-4.3", topics: ["EM Spectrum", "Interference", "Harmonics", "Beats"] },
        { title: "Doppler Effect", weeks: "Week 5-6", strand: "PHY4", subStrand: "4.4", topics: ["Apparent Frequency", "Sonic Boom", "Red Shift"] },
        { title: "Atomic and Nuclear Physics", weeks: "Week 7-8", strand: "PHY5", subStrand: "5.1-5.2", topics: ["Bohr Model", "Rydberg's Formula", "Fission", "Fusion"] },
        { title: "Investigation, Revision & Exam", weeks: "Week 9-10", strand: "All", subStrand: "-", topics: ["Practical Investigation", "External Exam Prep"] }
      ],
      assessment: "External Examination (60%)"
    }
  }
};

export default physicsCurriculum;
